import React, { useState, useEffect, useRef } from "react";
import * as d3 from "d3";
import { data } from "../../assets/ne_50m_admin_0_countries";
import { FmdGood } from "@mui/icons-material";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useDimensions } from "../../utils/useDimensions";
import franceFlag from "../../assets/images/Flag_of_France.svg";
import switzerlandFlag from "../../assets/images/Flag_of_Switzerland.svg.png";
import polandFlag from "../../assets/images/Flag_of_Poland.svg";
import lithuaniaFlag from "../../assets/images/Flag_of_Lithuania.svg";
import usaFlag from "../../assets/images/Flag_USA.png";
import bosniaFlag from "../../assets/images/Flag_of_Bosnia_and_Herzegovina.svg.png";
import pakistanFlag from "../../assets/images/Flag_of_Pakistan.svg";
import australiaFlag from "../../assets/images/Flag_of_Australia_(converted).svg.webp";
import polandLandmark from "../../assets/images/poland-landmark.jpg";
import lithuaniaLandmark from "../../assets/images/lithunia-landmark.jpg";
import franceLandmark from "../../assets/images/eiffel-tower-france.png";
import switzerlandLandmark from "../../assets/images/switzerland-landmark.JPG";
import usaLandmark from "../../assets/images/usa-landmark.avif";
import bosniaLandmark from "../../assets/images/Bosnia and Herzegovina-landmark.jpg";
import pakistanLandmark from "../../assets/images/Minar-e-Pakistan-scaled.jpg";
import australiaLandmark from "../../assets/images/australian-landmark.webp";

const countryFlags = {
  "Poland": polandFlag,
  "Lithuania": lithuaniaFlag,
  "France":franceFlag,
  "Switzerland":switzerlandFlag,
  "USA":usaFlag,
  "Bosnia and Herzegovina":bosniaFlag,
  "Pakistan":pakistanFlag,
  "Australia":australiaFlag,
};

const countryLandmarks={
  "Poland": polandLandmark,
  "Lithuania": lithuaniaLandmark,
  "France": franceLandmark,
  "Switzerland":switzerlandLandmark,
  "USA": usaLandmark,
  "Bosnia and Herzegovina": bosniaLandmark,
  "Pakistan":pakistanLandmark,
  "Australia":australiaLandmark,
}

const ConnectionMap = ({ width, height, setHeightState }) => {
  const [projectionScale, setProjectionScale] = useState(250);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth });
  let midPoint = [];
  let additionalHeight = height > 900 ? 200 : 100;
  const projection = d3
    .geoMercator()
    .scale(projectionScale)
    .translate([window.innerWidth / 2, (height + additionalHeight) / 2]);
  const [selectedCountry, setSelectedCountry] = useState();
  const [countryAchievements, setCountryAchievements] = useState();

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;

      setWindowSize({ width: newWidth });

      // Adjust scale based on screen width
      if (newWidth < 400) {
        setProjectionScale(45); // Smaller scale for mobile
        setHeightState(400);
      } else if (newWidth < 450) {
        setProjectionScale(58);
        setHeightState(500);
      } else if (newWidth < 600) {
        setProjectionScale(60);
        setHeightState(500);
      } else if (newWidth < 900) {
        setProjectionScale(80);
        setHeightState(600);
      } else if (newWidth < 1400) {
        setHeightState(700);
        setProjectionScale(130);
      } else {
        setProjectionScale(290); // Default scale
        setHeightState(1100);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Call once to set initial value
    
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const countryProjects = [
    {
      country: "Poland",
      details: [
        "TREND lab at PORT Polish Center for Technology Development",
        "Collaborations with Psychiatry Hospital Lodz, Nencki Institute of Experimental Biology, and Medical University of Wroclaw.",
      ],
    },
    {
      country: "Pakistan",
      details: [
        "PLMS project to assess the serum and sperm signatures of childhood trauma in a population with high consanguinity: In collaboration with the SOS Village and Chughtai Labs Private Limited, Pakistan.",
      ],
    },
    {
      country: "Bosnia and Herzegovina",
      details: [
        "ACTION Bosnia project to investigate the intergenerational transmission of the effects of Srebrenica Genocide in collaboration with the University Clinical Center Tuzla, Medical Faculty of the University of Tuzla, and Plava Polyclinic.",
      ],
    },
    {
      country: "Australia",
      details: [
        "TREMENDOS project to examine the role of microglia in the protective effects of environmental enrichment in neurodegenerative disorders in collaboration with The Florey Institute, Melbourne.",
        "MUSE ACE project to explore the interaction of peripheral lipids and microglia in the susceptibility vs. resilience to the long-term behavioral effects of adverse childhood experiences in collaboration with The Florey Institute, Melbourne.",
      ],
    },
    {
      country: "France",
      details: [
        "EMPATHY project to investigate the role of early metabolic programming of hypothalamic microglia-neuronal interactions in the risk of eating disorders in collaboration with the University of Bordeaux.",
      ],
    },
    {
      country: "Lithuania",
      details: [
        "EMPATHY project to investigate the role of early metabolic programming of hypothalamic microglia-neuronal interactions in the risk of eating disorders in collaboration with the Vilnius Life Sciences Center.",
      ],
    },
    {
      country: "USA",
      details: [
        "Collaboration with Prof. Paul Schulz at the University of Texas Health Science Center in Houston, TX on multiple translational studies, including targeting neurodegenerative disorders via metabolic pathways.",
      ],
    },
    {
      country: "Switzerland",
      details: [
        "TREMENDOS project to examine the role of microglia in the protective effects of environmental enrichment in neurodegenerative disorders in collaboration with the University of Lausanne.",
        "MUSE ACE project to explore the interaction of peripheral lipids and microglia in the susceptibility vs. resilience to the long-term behavioral effects of adverse childhood experiences in collaboration with the University of Lausanne.",
        "EMPATHY project to investigate the role of early metabolic programming of hypothalamic microglia-neuronal interactions in the risk of eating disorders in collaboration with the Vilnius Life Sciences Center.",
      ],
    },
  ];

  const geoPathGenerator = d3.geoPath().projection(projection);
  const countryNames = [
    "Poland",
    "Pakistan",
    "Bosnia and Herzegovina",
    "Australia",
    "France",
    "Lithuania",
    "USA",
    "Switzerland",
  ];

  const countryCoords = countryNames.map((name) => {
    const countryFeature = data.features.find(
      (feature) => feature.properties.name === name
    );
    if (countryFeature) {
      let lon, lat;
      [lon, lat] = d3.geoCentroid(countryFeature);
      if (name === "France") {
        lon = 2.2137; // France’s actual longitude
        lat = 46.6034; // France’s actual latitude
      }
      return { name, coords: projection([lon, lat]) };
    }
    return { name, coords: null };
  });

  const poland = countryCoords.find((c) => c.name === "Poland");

  const lineGenerator = d3
    .line()
    .x((d) => d[0])
    .y((d) => d[1])
    .curve(d3.curveBasis);

  return (
    <div className="w-full flex justify-center items-center">
      <svg width={"100%"} height={height}>
        {/* Countries  */}
        {data.features
          .filter((shape) => shape.id !== "ATA")
          .map((shape) => {
            const isInteractive = countryNames.includes(shape.properties.name);
            return (
              <path
                key={shape.id}
                d={geoPathGenerator(shape)}
                stroke="lightGrey"
                strokeWidth={0.5}
                fill="#a1adaf"
                fillOpacity={0.7}
                style={{
                  cursor: isInteractive ? "pointer" : "default",
                }}
                onClick={() => {
                  if (isInteractive) {
                    setSelectedCountry(shape.properties.name);
                  }
                }}
              />
            );
          })}

        {/* Dashed Lines */}
        {poland?.coords &&
          countryCoords.map(({ name, coords }, index) => {
            if (coords && name !== "Poland") {
              if (name === "Australia") {
                midPoint = [
                  (coords[0] + poland.coords[0]) / 2 + 80,
                  (coords[1] + poland.coords[1]) / 2 - 40,
                ];
              } else {
                midPoint = [
                  (coords[0] + poland.coords[0]) / 2,
                  (coords[1] + poland.coords[1]) / 2 - 40,
                ];
              }
              const pathData = lineGenerator([coords, midPoint, poland.coords]);

              return (
                <path
                  key={`line-${index}`}
                  d={pathData}
                  stroke="white"
                  strokeWidth={2}
                  fill="none"
                  opacity={0.7}
                />
              );
            }
            return null;
          })}

        {/* Pin Points  */}
        {countryCoords?.map(({ name, coords }, index) => {
          if (coords) {
            const [x, y] = coords;
            if (name === "Poland") {
              return (
                <foreignObject
                  key={index}
                  x={x - 10}
                  y={y - 20}
                  width="60"
                  height="65"
                  style={{
                    overflow: "visible",
                    cursor: "pointer",
                    pointerEvents: "auto",
                  }}
                  onMouseEnter={() => setSelectedCountry(name)}
                >
                  <div
                    style={{
                      transform: "scale(1.4)",
                      color: "#6d0ecc",
                    }}
                  >
                    <FmdGood />
                  </div>
                </foreignObject>
              );
            } else {
              return (
                <foreignObject
                  key={index}
                  x={x - 15}
                  y={y - 20}
                  width="30"
                  height="35"
                  onMouseEnter={() => setSelectedCountry(name)}
                  style={{ cursor: "pointer", pointerEvents: "auto" }}
                >
                  <div
                    style={{
                      transform: "scale(0.8)",
                      color: "#2075d6",
                    }}
                  >
                    <FmdGood />
                  </div>
                </foreignObject>
              );
            }
          }
          return null;
        })}
      </svg>
      <Dialog open={!!selectedCountry} onClose={() => setSelectedCountry(null)}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: "1rem",
            marginTop: "30px",
            padding: "0 1rem",
          }}
        >
          <img
            src={countryFlags[selectedCountry]}
            alt={`${selectedCountry} flag`}
            style={{
              width: "100%",
              maxWidth: "280px",
              height: "auto",
              aspectRatio: "280/150",
              objectFit: "cover",
              borderRadius: "4px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          />
          <img
            src={countryLandmarks[selectedCountry]}
            alt={`${selectedCountry} landmark`}
            style={{
              width: "100%",
              maxWidth: "280px",
              height: "auto",
              aspectRatio: "280/150",
              objectFit: "cover",
              borderRadius: "4px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          />
        </div>
        <DialogTitle
          style={{
            fontFamily: "Montserrat",
            textAlign: "center",
            padding: "1rem 1rem 0 1rem",
          }}
        >
          <span>{selectedCountry}</span>
        </DialogTitle>
        <DialogContent
          style={{
            padding: "1rem",
          }}
        >
          {countryProjects.find((c) => c.country === selectedCountry)
            ?.details ? (
            <ul
              style={{
                paddingLeft: "20px",
                listStyleType: "square",
                margin: 0,
              }}
            >
              {countryProjects
                .find((c) => c.country === selectedCountry)
                .details.map((detail, index) => (
                  <li
                    key={index}
                    style={{
                      marginBottom: "0.5rem",
                      lineHeight: "1.5",
                    }}
                  >
                    {detail}
                  </li>
                ))}
            </ul>
          ) : (
            <p style={{ textAlign: "center" }}>No details available.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConnectionMap;
